/**
 * De tabbladen boven een lijst met huishoudens of deelnemers.
 *
 * Drie regels, en de tweede is de reden dat dit een aparte functie is:
 *
 * 1. **De canonieke volgorde wint.** Die volgorde ís de trechter en stuurt ook
 *    de balkvolgorde in elke statusgrafiek; de instelling mag hem niet omgooien.
 * 2. **Een verborgen status krijgt tóch een tab zolang er nog iets op staat.**
 *    Anders verbergt de instelling geen woord maar gegevens: zonder tab is dat
 *    huishouden nergens meer te bereiken, en 'Alle' telt het wél mee — dus de
 *    tellingen spreken elkaar tegen zonder dat iemand ziet waarom.
 * 3. **De tab waar de gebruiker nu naar kijkt blijft staan**, ook als hij leeg
 *    en verborgen is. Die keuze komt van de dashboardkaart via `?status=`, en
 *    een lijst zonder aangewezen tab leest als een storing.
 *
 * AI-CONTEXT: See docs/decisions.md#adr-032
 * AI-CONTEXT: See docs/features/tenant-status-and-column-config.md
 */

/**
 * @typedef {object} StatusTab
 * @property {string} id - Statussleutel, of 'all'
 * @property {string} label - Wat de gebruiker leest
 * @property {number} count - Aantal records op die status
 */

/**
 * Welke statussen er getoond worden, in canonieke volgorde.
 *
 * Apart en geëxporteerd omdat het bord dezelfde regel nodig heeft. Twee plekken
 * die los beslissen welke statussen bestaan, gaan uiteenlopen — en dan toont de
 * tabel een emmer die het bord niet heeft, of andersom.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {object} params
 * @param {object} params.display - Uitkomst van `resolveStatusDisplay`
 * @param {Record<string, number>} params.counts - Aantal per statussleutel
 * @param {string} [params.alsoKeep] - Een sleutel die hoe dan ook blijft (de actieve tab)
 * @returns {Array<string>}
 */
const shownStatusKeys = ({ display, counts, alsoKeep }) => {
    const { keys = [], visibleKeys = [] } = display || {}
    const zichtbaar = new Set(visibleKeys)
    const aantallen = counts || {}

    return keys.filter((key) => zichtbaar.has(key) || aantallen[key] > 0 || key === alsoKeep)
}

/**
 * @param {object} params
 * @param {object} params.display - Uitkomst van `resolveStatusDisplay`
 * @param {Record<string, number>} params.counts - Aantal per statussleutel
 * @param {string} [params.selectedKey] - De tab die nu actief is
 * @param {number} params.total - Aantal records in totaal, voor de tab 'Alle'
 * @param {string} [params.allLabel] - Opschrift van de laatste tab
 * @returns {Array<StatusTab>}
 */
const buildStatusTabs = ({ display, counts, selectedKey, total, allLabel = 'Alle' }) => {
    const { labels = {} } = display || {}
    const aantallen = counts || {}

    const tabs = shownStatusKeys({ display, counts, alsoKeep: selectedKey })
        .map((key) => ({
            id: key,
            label: labels[key],
            count: aantallen[key] || 0,
        }))

    return [...tabs, { id: 'all', label: allLabel, count: total || 0 }]
}

/**
 * Telt records per statussleutel in één pass.
 *
 * Sleutels die niet in de canonieke lijst staan tellen niet mee — die horen bij
 * geen enkele tab, en stilzwijgend een emmer bijmaken zou de trechter oprekken
 * met iets wat de rest van de app niet kent.
 *
 * @param {Array<{Status?: string}>} items
 * @param {Array<string>} keys - `ADRESS_STATUS_KEYS` of `PERSONA_STATUS_KEYS`
 * @returns {Record<string, number>}
 */
const countByStatus = (items, keys) => {
    const counts = (keys || []).reduce((acc, key) => {
        acc[key] = 0
        return acc
    }, {})

    ;(Array.isArray(items) ? items : []).forEach((item) => {
        if (counts[item?.Status] !== undefined) counts[item.Status] += 1
    })

    return counts
}

export { countByStatus, shownStatusKeys }
export default buildStatusTabs
